import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Card, CardContent, Radio } from '@mui/material';
import { PeakPreset, PEAK_PRESETS } from './presets';
import PresetPreviewChart from './PresetPreviewChart';
import { generateTimeLabels } from './utils';

interface PeakPresetSelectorProps {
  selectedPreset: string;
  onPresetChange: (presetId: string, peaks: any[]) => void;
  startTime: string;
  endTime: string;
  interval: number;
}

const PeakPresetSelector: React.FC<PeakPresetSelectorProps> = ({
  selectedPreset,
  onPresetChange,
  startTime,
  endTime,
  interval
}) => {
  const { t } = useTranslation('mapWizards');
  const timeLabels = generateTimeLabels(startTime, endTime, interval);

  const handlePresetSelect = (preset: PeakPreset) => {
    onPresetChange(preset.id, preset.peaks);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('generateRides.peakPresets.intro')}
      </Typography>
      
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: 1.5 
      }}>
        {PEAK_PRESETS.map((preset) => (
          <Card
            key={preset.id}
            sx={{
              cursor: 'pointer',
              border: selectedPreset === preset.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
              backgroundColor: selectedPreset === preset.id ? '#f3f8ff' : '#ffffff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: '#f8fbff',
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
            onClick={() => handlePresetSelect(preset)}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Radio
                  checked={selectedPreset === preset.id}
                  sx={{ p: 0, mr: 0.5 }}
                  size="small"
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {preset.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PresetPreviewChart
                  peaks={preset.peaks}
                  timeLabels={timeLabels}
                  selected={selectedPreset === preset.id}
                  width={70}
                  height={35}
                />
              </Box>
              
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem' }}>
                {preset.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default PeakPresetSelector; 